import { useAuthStore } from '../stores/auth';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const bearer = token && token !== 'cookie' ? token : '';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  const isForm = opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
  });
  if (res.status === 401 && !path.endsWith('/auth/refresh') && !path.endsWith('/auth/login')) {
    const renewed = await fetch('/client/v1/auth/refresh', { method: 'POST', credentials: 'include' });
    if (!renewed.ok) useAuthStore.getState().logout();
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = data?.error;
    throw new ApiError(res.status, err?.code ?? 'http_error', err?.message ?? res.statusText);
  }
  return data as T;
}

export const api = {
  get: <T,>(p: string) => request<T>(p),
  post: <T,>(p: string, body?: unknown) => request<T>(p, { method: 'POST', body }),
  postForm: <T,>(p: string, form: FormData) => request<T>(p, { method: 'POST', body: form }),
  patch: <T,>(p: string, body?: unknown) => request<T>(p, { method: 'PATCH', body }),
  put: <T,>(p: string, body?: unknown) => request<T>(p, { method: 'PUT', body }),
  delete: <T,>(p: string) => request<T>(p, { method: 'DELETE' }),
};

/** Authenticated file download (ZIP / XML / CSV). Filename comes from Content-Disposition. */
export async function downloadAuthorized(path: string, fallbackName: string): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const bearer = token && token !== 'cookie' ? token : '';
  const headers: Record<string, string> = { Accept: '*/*' };
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  const res = await fetch(path, { headers, credentials: 'include' });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const err = data?.error;
    throw new ApiError(res.status, err?.code ?? 'http_error', err?.message ?? res.statusText);
  }
  const disp = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disp);
  const filename = match?.[1] || fallbackName;
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4_000);
}
